import { memo } from 'react';

import ActionBar from '@vimeo/clip-ui/components/ActionBar';
import { useDeviceDetection } from '@vimeo/viewer-showcase-ui/hooks/use-device-detection';

import { LoginModal } from '../hooks/use-login-modal';
import { useReportVideoModalState } from '../hooks/use-report-modal-state';
import { useVideoActionBarModule } from '../hooks/use-video-action-bar-module';
import { useAlbumStore } from '../state/albumStore';
import { VIDEO_MODAL_TYPE } from '../utils/constants';
import { VideoActionBarModuleProps } from '../utils/types';
import { DownloadListModule } from './DownloadListModule';
import { ReportVideoModalModule } from './ReportVideoModalModule';
import { ShareModalModule, useShareModalState } from './ShareModalModule';

export const VideoActionBarModule = memo((props: VideoActionBarModuleProps) => {
  const { shareModalActive, setShareModalActive, shareModalState } = useShareModalState(false);
  const { reportModalOpen, setReportModalOpen } = useReportVideoModalState();
  const albumId = useAlbumStore((state) => state.albumId);
  const { isMobile } = useDeviceDetection();
  const {
    actionBarStateConfig,
    actionBarDisplayConfig,
    actionBarHandlers,
    activeDownloadClipId,
    publicCommentsCount,
    setActiveDownloadClipId,
    shouldShowActionBar,
    onShareClick,
    onLoginRequiredInShareModal,
  } = useVideoActionBarModule({
    ...props,
    shareModalActive,
    setShareModalActive,
    setReportModalOpen,
  });

  if (!shouldShowActionBar) {
    return null;
  }

  return (
    <>
      <ActionBar
        totalComments={publicCommentsCount}
        disableScroll
        disableTooltips={isMobile}
        {...actionBarStateConfig}
        {...actionBarHandlers}
        {...actionBarDisplayConfig}
      />
      {actionBarDisplayConfig.showShare ? (
        <ShareModalModule
          uri={`video/${props.clipRequestId}`}
          active={shareModalActive}
          modalState={shareModalState}
          onClose={onShareClick}
          onLoginRequired={onLoginRequiredInShareModal}
          shared={{
            hideTimestampCheckbox: true,
            type: VIDEO_MODAL_TYPE.ALBUM_VIDEO,
            clipId: props.clipId,
            albumId: albumId,
          }}
        />
      ) : null}
      {/* For compliance reasons we must always show the report button see CSVPRO-759 for context */}
      <ReportVideoModalModule
        clipId={props.clipId}
        isOpen={reportModalOpen}
        onClose={actionBarHandlers.onReportClose}
      />
      <DownloadListModule
        isDownloadModalOpen={Boolean(activeDownloadClipId)}
        onClose={() => setActiveDownloadClipId(null)}
        clipId={activeDownloadClipId}
      />
      <LoginModal />
    </>
  );
});
